import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  GetEventAccountDocument,
  GetEventAccountQuery,
} from '../../../../graphql/queries/getEventAccount.generated';
import {
  useAllEventsPaginatedQuery,
  AllEventsPaginatedDocument,
} from '../../../../graphql/queries/allEventsPaginated.generated';
import { useAddEventAccountToEventParticipantsMutation } from '../../../../graphql/mutations/addEventAccountToEventParticipants.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import { useRemoveEventAccountFromEventParticipantsMutation } from '../../../../graphql/mutations/removeEventAccountFromEventParticipants.generated';
import {
  Button,
  ListGroup,
  ListGroupItem,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import i18n from '@/utils/i18n';
import { FaSearch } from 'react-icons/fa';
import CustomPagination from '../../../../components/CustomPagination';
import { isDev, isLocalDev } from '@/utils/isDev';
import { AppConfig } from '@/utils/config';

const EVENTS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

interface EventParticipationsTabProps {
  eventAccountId: string;
  eventAccount: GetEventAccountQuery['eventAccount'];
}

export const EventParticipationsTab: React.FC<EventParticipationsTabProps> = ({
  eventAccountId,
  eventAccount,
}) => {
  // Pagination and filtering states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(''); // For UI display
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // For actual API calls
  const [showPastEvents, setShowPastEvents] = useState(false);

  // Loading state management for participation actions
  const [isParticipationLoading, setIsParticipationLoading] = useState(false);
  const participationLoadingStartTimeRef = useRef<number | null>(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setDebouncedSearchTerm(value);
          setCurrentPage(1); // Reset to first page when searching
        }, SEARCH_DEBOUNCE_MS);
      };
    })(),
    [],
  );

  const {
    data: allEventsData,
    loading: loadingEvents,
    error: eventsError,
  } = useAllEventsPaginatedQuery({
    variables: {
      filter: {
        search: debouncedSearchTerm || undefined,
        show_past_events: showPastEvents,
      },
      skip: (currentPage - 1) * EVENTS_PER_PAGE,
      take: EVENTS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  if (eventsError) {
    LayoutStoreInstance.setError(eventsError.message);
  }

  const [
    addEventAccountToEventParticipants,
    { loading: addingToParticipants },
  ] = useAddEventAccountToEventParticipantsMutation();
  const [
    removeEventAccountFromEventParticipants,
    { loading: removingFromParticipants },
  ] = useRemoveEventAccountFromEventParticipantsMutation();

  const disableParticipationEditing =
    addingToParticipants || removingFromParticipants || isParticipationLoading;

  // Helper function to handle minimum loading time for participation actions
  const handleParticipationAction = async (action: () => Promise<any>) => {
    setIsParticipationLoading(true);
    participationLoadingStartTimeRef.current = Date.now();

    try {
      await action();
    } finally {
      const elapsedTime =
        Date.now() - participationLoadingStartTimeRef.current!;
      const remainingTime = Math.max(0, 1000 - elapsedTime); // 1s minimum

      setTimeout(() => {
        setIsParticipationLoading(false);
        participationLoadingStartTimeRef.current = null;
      }, remainingTime);
    }
  };

  const handleAddToEvent = async (eventId: string) => {
    await handleParticipationAction(async () => {
      const result = await addEventAccountToEventParticipants({
        variables: { event_account_id: eventAccountId, event_id: eventId },
        refetchQueries: [
          {
            query: GetEventAccountDocument,
            variables: { id: eventAccountId },
          },
          {
            query: AllEventsPaginatedDocument,
            variables: {
              filter: {
                search: debouncedSearchTerm || undefined,
                show_past_events: showPastEvents,
              },
              skip: (currentPage - 1) * EVENTS_PER_PAGE,
              take: EVENTS_PER_PAGE,
            },
          },
        ],
      });
      if (result.errors) {
        LayoutStoreInstance.setError('Error adding to event');
      } else {
        LayoutStoreInstance.setSuccess('Added to event');
      }
    });
  };

  const handleRemoveFromEvent = async (eventId: string) => {
    await handleParticipationAction(async () => {
      const result = await removeEventAccountFromEventParticipants({
        variables: { event_account_id: eventAccountId, event_id: eventId },
        refetchQueries: [
          {
            query: GetEventAccountDocument,
            variables: { id: eventAccountId },
          },
          {
            query: AllEventsPaginatedDocument,
            variables: {
              filter: {
                search: debouncedSearchTerm || undefined,
                show_past_events: showPastEvents,
              },
              skip: (currentPage - 1) * EVENTS_PER_PAGE,
              take: EVENTS_PER_PAGE,
            },
          },
        ],
      });
      if (result.errors) {
        LayoutStoreInstance.setError('Error removing from event');
      } else {
        LayoutStoreInstance.setSuccess('Removed from event');
      }
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value); // Update UI immediately
    debouncedSearch(value); // Debounced API call
  };

  const handleShowPastEventsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setShowPastEvents(e.target.checked);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const events = allEventsData?.eventsPaginated?.events || [];
  const totalEvents = allEventsData?.eventsPaginated?.total || 0;
  const pageCount = Math.ceil(totalEvents / EVENTS_PER_PAGE);
  const participatingInEvents = eventAccount?.participating_in_events || [];
  const enableEventAccountParticipatingEdit =
    !AppConfig.PARTICIPATING_EVENT_ACCOUNT_SYNC_ENABLED; // isDev() || isLocalDev();

  return (
    <div>
      <h4 className="tw:mb-3">{i18n.t('EventAccount:eventParticipations')}</h4>
      {!enableEventAccountParticipatingEdit && (
        <Card className="tw:mb-4">
          <CardBody>
            <p className="tw:mb-0">
              {i18n.t('EventAccount:changeEventParticipationInZoho')}
            </p>
            {enableEventAccountParticipatingEdit && (
              <p className="tw:mb-0">
                Changing Event Participation in the Admin Frontend is only
                enabled in dev mode.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {loadingEvents || isParticipationLoading ? (
        <div className="tw:py-4 tw:text-center">
          <div
            className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:text-primary"
            role="status"
          >
            <span className="tw:sr-only">{i18n.t('Common:loading')}</span>
          </div>
        </div>
      ) : (
        <>
          {!enableEventAccountParticipatingEdit && (
            <>
              {/* Read-only list of participating events (production mode) */}
              {participatingInEvents.length > 0 ? (
                <ListGroup className="tw:mb-4">
                  {participatingInEvents.map((event) => {
                    if (!event || !event.id || !event.name) {
                      return null;
                    }

                    return (
                      <ListGroupItem key={event.id}>
                        <Link
                          to={'/event/' + event.id}
                          className="tw:no-underline"
                        >
                          <strong>{event.name}</strong>
                        </Link>
                        {(event.start_date || event.end_date) && (
                          <div className="tw:text-sm tw:text-neutral-400 tw:mt-1">
                            {event.start_date && (
                              <span>
                                {new Date(
                                  event.start_date,
                                ).toLocaleDateString()}
                              </span>
                            )}
                            {event.start_date && event.end_date && (
                              <span> - </span>
                            )}
                            {event.end_date && (
                              <span>
                                {new Date(event.end_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              ) : (
                <p className="tw:text-neutral-400">
                  {i18n.t('EventAccount:notParticipating')}
                </p>
              )}
            </>
          )}
        </>
      )}

      {enableEventAccountParticipatingEdit && (
        <Card className="tw:mb-4">
          <CardBody>
            <Row className="tw:items-end">
              <Col md={6}>
                <FormGroup>
                  <Label for="eventSearch">
                    <strong>{i18n.t('Common:filter')}</strong>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <FaSearch />
                    </InputGroupText>
                    <Input
                      id="eventSearch"
                      type="text"
                      placeholder={i18n.t('EventAccount:searchEvents')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <div
                    className="tw:flex tw:items-center"
                    style={{ height: 'auto', padding: '0.375rem 0.75rem' }}
                  >
                    <Input
                      type="checkbox"
                      id="showPastEvents"
                      checked={showPastEvents}
                      onChange={handleShowPastEventsChange}
                      className="tw:mr-2"
                    />
                    <Label check for="showPastEvents" className="tw:mb-0">
                      {i18n.t('EventAccount:showPastEvents')}
                    </Label>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {enableEventAccountParticipatingEdit && (
        <>
          {loadingEvents || isParticipationLoading ? (
            <div className="tw:py-4 tw:text-center">
              <div
                className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:text-primary"
                role="status"
              >
                <span className="tw:sr-only">{i18n.t('Common:loading')}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="tw:mb-3">
                <small className="tw:text-neutral-400">
                  {i18n.t('Common:showing')} {events.length}{' '}
                  {i18n.t('Common:of')} {totalEvents}{' '}
                  {i18n.t('EventAccount:events')}
                  {debouncedSearchTerm && (
                    <span>
                      {' '}
                      {i18n.t('Common:for')} "{debouncedSearchTerm}"
                    </span>
                  )}
                </small>
              </div>

              {events.length > 0 ? (
                <ListGroup className="tw:mb-4">
                  {events.map((event) => {
                    // Defensive check to ensure event object is valid
                    if (!event || !event.id || !event.name) {
                      return null;
                    }

                    return (
                      <ListGroupItem
                        key={event.id}
                        className="tw:flex tw:justify-between tw:items-center"
                      >
                        <div>
                          <Link
                            to={
                              '/event/' +
                              event.id +
                              '/add-account/' +
                              eventAccountId
                            }
                            className="tw:no-underline"
                          >
                            <strong>{event.name}</strong>
                          </Link>
                          {event.description && (
                            <div className="tw:text-sm tw:text-neutral-400 tw:mt-1">
                              {event.description}
                            </div>
                          )}
                          {(event.start_date || event.end_date) && (
                            <div className="tw:text-sm tw:text-neutral-400 tw:mt-1">
                              {event.start_date && (
                                <span>
                                  {new Date(
                                    event.start_date,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                              {event.start_date && event.end_date && (
                                <span> - </span>
                              )}
                              {event.end_date && (
                                <span>
                                  {new Date(
                                    event.end_date,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="tw:flex tw:items-center">
                          {participatingInEvents.find(
                            (participatingEvent) =>
                              participatingEvent.id === event.id,
                          ) ? (
                            <>
                              <span className="tw:inline-flex tw:items-center tw:px-2 tw:py-1 tw:text-xs tw:font-semibold tw:rounded tw:bg-success-main tw:text-white tw:mr-2">
                                {i18n.t('Common:participating')}
                              </span>
                              <Button
                                size="sm"
                                color="danger"
                                disabled={disableParticipationEditing}
                                onClick={() => handleRemoveFromEvent(event.id)}
                              >
                                {i18n.t('Common:remove')}
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              color="primary"
                              disabled={disableParticipationEditing}
                              onClick={() => handleAddToEvent(event.id)}
                            >
                              {i18n.t('Common:add')}
                            </Button>
                          )}
                        </div>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              ) : (
                <div className="tw:py-4 tw:text-center">
                  <p className="tw:text-neutral-400">
                    {debouncedSearchTerm
                      ? i18n.t('EventAccount:noEventsFound')
                      : i18n.t('EventAccount:noEventsAvailable')}
                  </p>
                </div>
              )}

              {pageCount > 1 && (
                <div className="tw:flex tw:justify-center">
                  <CustomPagination
                    currentPage={currentPage}
                    pageCount={pageCount}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
